/**
 * SoftPresence - P5.js Interactive Art Project
 *
 * A shader-based visualization that responds to user interactions
 * and connects multiple viewers through Socket.IO.
 */ let canvas;
let userHash;
let theShader;
let socket;
let gui;
let allTogether = {
    count: 0,
    users: []
};
// Create a consistent message template with all possible fields
let messageTemplate = null;
// Debug Flag
const _DEBUG = false;
// State object for tracking interaction states
const state = {
    art_scope: 0,
    scroll_lerping_current: false,
    lerping_direction: true,
    l1_up: false,
    l2_up: false,
    l1_lerping_current: false,
    l2_lerping_current: false,
    click_t1_turbulance: false,
    click_t2_turbulance: false
};
// LERPING variables
let up = true;
let increment, ceiling, tmp_shader_speed;
let l1_increment, l1_ceiling, l1_floor, l1_map, l1_tmp;
let l2_increment, l2_ceiling, l2_floor, l2_map, l2_tmp;
let t1_up, t2_up, t1_increment, t2_increment;
let t1_ceiling, t1_floor, t2_ceiling, t2_floor;
let tmp_t1_turb, tmp_t2_turb;
// Initialize variables for mouse influence
let mouseInfluenceActive = false;
let mouseInfluenceDecay = 0.95; // How quickly mouse influence fades
let mouseInfluenceStrength = 0.0; // Current mouse influence strength
let prevMouseX = 0; // Previous mouse X position
let mouseSpeed = 0; // Mouse movement speed
let baseL1Scale, baseL2Scale; // Store base scale values
// Variables for remote mouse influence management
let remoteInfluenceLock = false;
let remoteAnimationState = null; // Will track the animation state
let remoteAnimationActive = false;
// Configuration values
const densityConfig = {
    amplitude: {
        min: 0.18,
        max: 0.7
    },
    baseNoise: {
        min: 0.062,
        max: 0.09
    },
    // Initial values - these will adjust automatically
    initialSoftCap: 10,
    adaptationRate: 0.05,
    minSoftCap: 10,
    maxSoftCap: 100
};
// Variables to track visitor patterns
let dynamicSoftCap = densityConfig.initialSoftCap;
let peakVisitorCount = 1;
// Initialize Socket.IO connection
function setupSocket() {
    userHash = initSession();
    // Generate a fingerprint for location tracking
    //   const fingerprint = generateDeviceFingerprint();
    // Auto-determine environment based on hostname
    const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    const devKey = isLocal ? localStorage.getItem("DEV_KEY") || "" : null;
    // Get the server URL
    const serverUrl = getServerUrl();
    // Connect to the server
    socket = io(serverUrl, {
        transports: [
            "websocket"
        ],
        query: devKey ? {
            devKey
        } : {}
    });
    // Create the message template
    messageTemplate = {
        // Metadata
        client: "soft-presence",
        v_user: userHash,
        user_agent: navigator.userAgent,
        screen_size: `${window.screen.width}x${window.screen.height}`,
        path: window.location.pathname + window.location.hash,
        timestamp: Date.now(),
        close: 0,
        _sp: false,
        // Interaction data
        interactions: {
            scroll: 0,
            scroll_pos: 0,
            scroll_active: false,
            mouse_influence: 0,
            mouse: [
                0,
                0
            ],
            move: false,
            click: 0
        }
    };
    // Set up Socket.IO listeners
    setupSocketListeners();
    // Set up event listeners
    setupEventListeners(messageTemplate);
    // Start the garbage collector
    setInterval(garbageCleaner, 90000);
    // Fetch statistics initially and periodically
    fetchStatistics();
    setInterval(fetchStatistics, 30000); // Update every 30 seconds
    return messageTemplate;
}
// Setup Socket.IO listeners
function setupSocketListeners() {
    // Connection successful
    socket.on("connect", ()=>{
        if (_DEBUG) console.log("\u2705 Connected to server:", socket.id);
        document.getElementById("user-count").textContent = "1"; // Initialize with 1 (self)
        // Send location data after connection
        sendUserLocationData();
        // Update location data every 5 minutes to keep it fresh
        setInterval(sendUserLocationData, 300000);
        // Also update the viewer-count in the stats overlay
        const viewerCountElement = document.getElementById("viewer-count");
        if (viewerCountElement) viewerCountElement.textContent = "1";
        // Join the channel immediately after connecting
        joinRoom();
    });
    // Add debugging listeners
    socket.on("connect_error", (err)=>{
        console.error("\u274C Connection error:", err);
    });
    socket.on("disconnect", (reason)=>{
        if (_DEBUG) console.log("\u274C Disconnected:", reason);
        document.getElementById("user-count").textContent = "0";
        // Also update the viewer-count in the stats overlay
        const viewerCountElement = document.getElementById("viewer-count");
        if (viewerCountElement) viewerCountElement.textContent = "0";
    });
    // Listen for room-join confirmation
    socket.on("joined-room", (data)=>{
        if (_DEBUG) console.log("\u2705 Joined room confirmation:", data);
        if (data.clients) {
            allTogether.count = data.clients.length;
            document.getElementById("user-count").textContent = allTogether.count;
            // Also update the viewer-count in the stats overlay
            const viewerCountElement = document.getElementById("viewer-count");
            if (viewerCountElement) viewerCountElement.textContent = allTogether.count.toString();
            // Update users list
            allTogether.users = data.clients.map((clientId)=>({
                    v_user: clientId,
                    timestamp: Date.now()
                }));
        }
    });
    // Listen for messages
    socket.on("message", (data)=>{
        if (_DEBUG) console.log("\uD83D\uDCE9 Incoming message:", data);
        // Find if user is already in our list
        let c_user = data.sender;
        let c = _.findIndex(allTogether.users, function(o) {
            return o.v_user == c_user;
        });
        // Add new user or update existing user
        if (c == -1) {
            allTogether.users.push({
                v_user: c_user,
                timestamp: Date.now(),
                interactions: data.content.interactions || {}
            });
            allTogether.count++;
        } else {
            // Update existing user's info
            allTogether.users[c].timestamp = Date.now();
            allTogether.users[c].interactions = data.content.interactions || {};
        }
        // Update user count display
        document.getElementById("user-count").textContent = allTogether.count;
        // Also update the viewer-count in the stats overlay
        const viewerCountElement = document.getElementById("viewer-count");
        if (viewerCountElement) viewerCountElement.textContent = allTogether.count.toString();
        // Process the message if it's from another user and we're in global mode
        if (c_user !== socket.id && state.art_scope == 0) {
            const content = data.content;
            if (content.interactions && content.interactions.scroll_active) state.scroll_lerping_current = true;
            if (content.interactions && content.interactions.move) handleReceivedMouseInteractions(content);
            if (content.interactions && content.interactions.click == 1) {
                state.t1_up = true;
                state.t2_up = true;
                state.click_t1_turbulance = true;
                state.click_t2_turbulance = true;
                // Update cloud pulses count in stats overlay
                incrementCloudPulses();
            }
        }
        if (data.sender === socket.id && !data.fromRoom) {
            if (_DEBUG) console.log("\uD83D\uDD04 Auto-rejoining room after receiving self-message");
            joinRoom();
        }
    });
    socket.on("error", (err)=>{
        console.error("\u26A0\uFE0F Socket error:", err);
    });
}
// Function to join room
function joinRoom() {
    if (socket && socket.connected) {
        if (_DEBUG) console.log(`\u{1F504} Joining room: ${config.socket.project_id}-${config.socket.channel}`);
        socket.emit("join-channel", {
            project_id: config.socket.project_id,
            channel: config.socket.channel
        });
    } else console.error("\u274C Cannot join room: Socket not connected");
}
// Helper functions for updating and sending messages
function updateMessage(message, updates) {
    // Update timestamp for each change
    message.timestamp = Date.now();
    // Apply all updates from the provided object
    Object.entries(updates).forEach(([key, value])=>{
        if (key === "interactions") // For interactions, merge rather than replace
        Object.assign(message.interactions, value);
        else message[key] = value;
    });
    return message; // Return the updated message for chaining
}
// Function to publish a message
function publishMessage(message) {
    if (socket && socket.connected && state.art_scope == 0) socket.emit("message", {
        project_id: config.socket.project_id,
        channel: config.socket.channel,
        content: message
    });
}
// Set up event listeners for user interactions
function setupEventListeners(message) {
    // Throttled publish function
    const throttlePublishMessage = _.throttle(function() {
        publishMessage(message);
    }, 250);
    // Window close event
    window.addEventListener("beforeunload", (event)=>{
        message.close = 1;
        if (state.art_scope == 0 && socket && socket.connected) socket.emit("message", {
            project_id: config.socket.project_id,
            channel: config.socket.channel,
            content: message
        });
    });
    // Mouse move event
    window.addEventListener("mousemove", _.throttle((e)=>{
        updateMessage(message, {
            interactions: {
                mouse: [
                    e.screenX,
                    e.screenY
                ],
                scroll_pos: window.scrollY,
                scroll_active: false,
                move: true,
                click: 0
            }
        });
        if (state.art_scope == 0) throttlePublishMessage();
    }, 250));
    // Click event
    window.addEventListener("click", _.throttle((e)=>{
        // Only process clicks outside of UI elements
        const target = e.target;
        const isUIElement = target.closest(".controls") || target.closest(".header") || target.closest(".overlay") || target.closest(".c_overlay");
        if (!isUIElement) {
            updateMessage(message, {
                _sp: true,
                interactions: {
                    click: 1,
                    mouse: [
                        e.clientX,
                        e.clientY
                    ],
                    scroll_pos: window.scrollY,
                    scroll_active: false,
                    move: false,
                    mouse_influence: 0
                }
            });
            state.click_t1_turbulance = true;
            state.click_t2_turbulance = true;
            // Update cloud pulses count in stats overlay
            incrementCloudPulses();
            document.querySelectorAll("#last-pulse-time, #flyout-last-pulse span").forEach((el)=>{
                if (el) el.textContent = "just now";
            });
            // Also store the current time in local storage for later reference
            storage.set("last_pulse_time", Date.now());
            if (state.art_scope == 0) throttlePublishMessage();
        }
        // Reset after sending pulse message
        setTimeout(()=>{
            message._sp = false;
            message.interactions.click = 0;
        }, 300);
    }, 250));
    // Art scope selector change
    document.getElementById("art-scope").addEventListener("change", (e)=>{
        state.art_scope = parseInt(e.target.value);
    });
}
// Clean up inactive users
function garbageCleaner() {
    let rightNow = Date.now();
    let delay = 300000; // 5 minutes
    // Remove inactive users
    var i = allTogether.users.length;
    while(i--)if (rightNow - allTogether.users[i].timestamp > delay) allTogether.users.splice(i, 1);
    // Update user count
    if (allTogether.users) {
        allTogether.count = allTogether.users.length;
        document.getElementById("user-count").textContent = allTogether.count;
        // Also update the viewer-count in the stats overlay
        const viewerCountElement = document.getElementById("viewer-count");
        if (viewerCountElement) viewerCountElement.textContent = allTogether.count.toString();
    }
    state.l1_lerping_current = true;
    state.l2_lerping_current = true;
    // Update locations count
    updateLocationCount();
}
// P5.js setup function
function setup() {
    // Create canvas that fills the window
    canvas = createCanvas(windowWidth, windowHeight, WEBGL);
    canvas.parent("art-container");
    // Check for debug parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const debugMode = urlParams.get("debug") === "true";
    // Set up dat.GUI for controls only if in debug mode
    if (debugMode) {
        // Set up dat.GUI for controls
        setupGui();
        document.querySelector(".controls").classList.remove("hidden");
    } else // If not in debug mode, hide any GUI that might have been created
    if (typeof gui !== "undefined" && gui) gui.destroy();
    // Ensure consistent rendering on high-DPI displays
    pixelDensity(1);
    // Set frame rate
    frameRate(30);
    // Create the shader using our M2-compatible version
    theShader = createShader(vertexShader, fragmentShader);
    noStroke();
    // Initialize Socket.IO
    setupSocket();
    // Initialize LERPING variables
    setupLerping();
    // Initialize stats
    initStats();
    // Initialize stats flyout
    setupStatsFlyout();
    // Apply the shader
    shader(theShader);
}
// Initialize the LERPING variables
function setupLerping() {
    // SPEED LERPING
    up = true;
    increment = config.animation.speed_increment;
    ceiling = config.speed.max;
    tmp_shader_speed = shaderParams.iSpeed;
    // SCALE LERPING
    l1_increment = config.animation.l1_increment;
    l1_ceiling = config.scale.l1_max;
    l1_floor = config.scale.l1_min;
    l1_map = 0;
    l1_tmp = shaderParams.L1Scale;
    l2_increment = config.animation.l2_increment;
    l2_ceiling = config.scale.l2_max;
    l2_floor = config.scale.l2_min;
    l2_map = 0;
    l2_tmp = shaderParams.L2Scale;
    // TURBULENCE LERPING
    t1_up = true;
    t2_up = true;
    t1_increment = config.animation.t1_increment;
    t2_increment = config.animation.t2_increment;
    t1_ceiling = config.turbulence.t1_max;
    t1_floor = config.turbulence.t1_min;
    t2_ceiling = config.turbulence.t2_max;
    t2_floor = config.turbulence.t2_min;
    tmp_t1_turb = shaderParams.L1Turbulance;
    tmp_t2_turb = shaderParams.L2Turbulance;
}
// Initialize stats overlay counters
function initStats() {
    // Initialize cloud pulse counter if not already in storage
    if (!storage.get("cloud_pulses")) storage.set("cloud_pulses", 0);
    if (!storage.get("last_pulse_time")) storage.set("last_pulse_time", Date.now());
    // Initialize locations set if not already in storage
    if (!storage.get("user_locations")) storage.set("user_locations", []);
    // Add current user's location to the set
    updateLocationCount();
    // Update the stats display
    updateStatsDisplay();
    // Set up interval to update the stats display
    setInterval(updateStatsDisplay, 30000); // Update every 30 seconds
}
// Increment cloud pulses counter
function incrementCloudPulses() {
    const currentPulses = storage.get("cloud_pulses") || 0;
    storage.set("cloud_pulses", currentPulses + 1);
    storage.set("last_pulse_time", Date.now());
    // Update the stats display
    updateStatsDisplay();
}
// Update location count based on approximate geolocation or IP
function updateLocationCount() {
    // Get stored locations
    fetchStatistics();
}
// Enhanced location tracking for more accurate statistics
function sendUserLocationData() {
    if (!socket || !socket.connected) return;
    // Create a more reliable fingerprint that's still anonymous
    const fingerprint = {
        userAgent: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        colorDepth: window.screen.colorDepth,
        // Add a unique session ID that persists for this session only
        sessionId: userHash
    };
    // Send this data to the server
    socket.emit("update_location", {
        project_id: config.socket.project_id,
        channel: config.socket.channel,
        fingerprint: fingerprint
    });
}
// Update the stats display
function updateStatsDisplay(stats) {
    if (!stats) return;
    // 1. Current viewers
    const viewerCount = allTogether.count;
    // Update both the main stats and flyout stats
    document.querySelectorAll("#viewer-count, #flyout-viewers span").forEach((el)=>{
        if (el) el.textContent = viewerCount;
    });
    // 2. Locations
    const locationCount = stats.current.locations || 0;
    // Update all location count elements
    document.querySelectorAll("#location-count, #flyout-locations span").forEach((el)=>{
        if (el) el.textContent = locationCount;
    });
    // 3. Today's pulses
    const todayPulses = stats.today.pulses || 0;
    // Update all pulse count elements for today
    document.querySelectorAll("#cloud-pulses, #flyout-pulses span").forEach((el)=>{
        if (el) el.textContent = todayPulses;
    });
    // 4. Total pulses (all time)
    const totalPulses = stats.totals.total_pulses || 0;
    document.querySelectorAll("#total-pulses, #flyout-total-pulses span").forEach((el)=>{
        if (el) {
            el.textContent = totalPulses;
            console.log(`Updated total pulses to ${totalPulses} in element:`, el);
        }
    });
    // 5. Last pulse time
    if (stats.lastPulse) {
        const lastPulseTime = new Date(stats.lastPulse);
        const now = new Date();
        const diffMs = now - lastPulseTime;
        const diffMinutes = Math.floor(diffMs / 60000);
        const diffSeconds = Math.floor(diffMs % 60000 / 1000);
        let timeText;
        if (diffMinutes < 1 && diffSeconds < 5) timeText = "just now";
        else if (diffMinutes < 1) timeText = `${diffSeconds} sec ago`;
        else if (diffMinutes === 1) timeText = "1 minute ago";
        else timeText = `${diffMinutes} minutes ago`;
        // Update all last pulse time elements
        document.querySelectorAll("#last-pulse-time, #flyout-last-pulse span").forEach((el)=>{
            if (el) el.textContent = timeText;
        });
    }
    // Log for debugging
    if (_DEBUG) console.log("Stats updated:", stats);
}
// Function to set up the stats flyout element
function setupStatsFlyout() {
    const statsIcon = document.getElementById("StatsIcon");
    const statsFlyout = document.getElementById("StatsFlyout");
    // Check for flyout elements and add if missing
    if (statsFlyout) {
        // Check for the total-pulses element and add if missing
        if (!document.getElementById("flyout-total-pulses")) {
            const totalPulsesRow = document.createElement("div");
            totalPulsesRow.className = "stats-row";
            totalPulsesRow.id = "flyout-total-pulses";
            totalPulsesRow.innerHTML = "<strong>All time:</strong> <span>0</span>";
            // Find where to insert it
            const lastPulseRow = document.getElementById("flyout-last-pulse");
            if (lastPulseRow) lastPulseRow.parentNode.insertBefore(totalPulsesRow, lastPulseRow);
            else {
                // If last-pulse row doesn't exist, append to content
                const statsContent = document.querySelector(".stats-content");
                if (statsContent) statsContent.appendChild(totalPulsesRow);
                else console.log("Could not find .stats-content element");
            }
        }
        // Set initial state for flyout
        statsFlyout.style.opacity = "0";
        statsFlyout.style.pointerEvents = "none";
        // Set up mouse events if both elements exist
        if (statsIcon) {
            // Mouse enter - fade in
            statsIcon.addEventListener("mouseenter", ()=>{
                // Remove hidden class but keep initial styles
                statsFlyout.classList.remove("hidden");
                // Force a repaint
                statsFlyout.offsetWidth;
                // Start animation
                fadeIn(statsFlyout, 300); // 300ms duration
                // Fetch fresh data on hover
                fetchStatistics();
            });
            // Mouse leave - fade out
            statsIcon.addEventListener("mouseleave", ()=>{
                fadeOut(statsFlyout, 300, ()=>{
                    statsFlyout.classList.add("hidden");
                });
            });
        }
    } else console.log("StatsFlyout element not found");
    // Always fetch initial stats regardless of flyout existence
    console.log("Fetching initial statistics");
    fetchStatistics();
    // Always set up interval for updates
    console.log("Setting up interval for stats updates");
    setInterval(fetchStatistics, 30000); // Update every 30 seconds
}
// Fade in function
function fadeIn(element, duration) {
    let opacity = 0;
    const startTime = performance.now();
    element.style.pointerEvents = "auto";
    function animate(currentTime) {
        const elapsed = currentTime - startTime;
        opacity = Math.min(elapsed / duration, 1);
        element.style.opacity = opacity;
        if (elapsed < duration) requestAnimationFrame(animate);
    }
    requestAnimationFrame(animate);
}
// Fade out function
function fadeOut(element, duration, callback) {
    let opacity = 1;
    const startTime = performance.now();
    element.style.pointerEvents = "none";
    function animate(currentTime) {
        const elapsed = currentTime - startTime;
        opacity = Math.max(1 - elapsed / duration, 0);
        element.style.opacity = opacity;
        if (elapsed < duration) requestAnimationFrame(animate);
        else if (callback) callback();
    }
    requestAnimationFrame(animate);
}
// P5.js draw function
function draw() {
    // Handle turbulence animations based on click
    handleTurbulenceAnimations();
    // Handle scroll animations
    handleScrollAnimations();
    // Handle layer scale animations based on user count
    handleLayerScaleAnimations();
    // Handle mouse interactions for cloud pushing effect
    handleMouseInteractions();
    // Handle remote mouse interactions
    updateRemoteAnimation();
    updateCloudDensityForVisitorCount(allTogether.count);
    // Update colors based on time of day
    // updateTimeBasedColors();
    // Set shader uniforms - mapping our shader params to the new M2-compatible shader
    theShader.setUniform("iResolution", [
        width,
        height
    ]);
    theShader.setUniform("iMouse", [
        mouseX,
        mouseY,
        0,
        0
    ]);
    theShader.setUniform("iTime", millis() * 0.001); // Convert to seconds
    // Set the global parameters
    theShader.setUniform("Amplitude", shaderParams.Amplitude);
    theShader.setUniform("Hvalue", shaderParams.Hvalue);
    theShader.setUniform("Hvalue2", shaderParams.Hvalue2);
    theShader.setUniform("uUVvalue", shaderParams.uUVvalue);
    theShader.setUniform("iSpeed", shaderParams.iSpeed);
    theShader.setUniform("iAlpha", shaderParams.iAlpha);
    theShader.setUniform("showGrid", shaderParams.showGrid);
    // Set Layer 1 parameters
    theShader.setUniform("Layer1CloudColour", shaderParams.Layer1CloudColour);
    theShader.setUniform("L1MoValue", shaderParams.L1MoValue);
    theShader.setUniform("L1Scale", shaderParams.L1Scale);
    theShader.setUniform("L1Turbulance", shaderParams.L1Turbulance);
    theShader.setUniform("L1Zoom", shaderParams.L1Zoom);
    theShader.setUniform("L1SmoothStep", shaderParams.L1SmoothStep);
    // Set Layer 2 parameters
    theShader.setUniform("Layer2CloudColour", shaderParams.Layer2CloudColour);
    theShader.setUniform("L2MoValue", shaderParams.L2MoValue);
    theShader.setUniform("L2Scale", shaderParams.L2Scale);
    theShader.setUniform("L2Turbulance", shaderParams.L2Turbulance);
    theShader.setUniform("L2Zoom", shaderParams.L2Zoom);
    theShader.setUniform("L2SmoothStep", shaderParams.L2SmoothStep);
    // Apply the shader
    shader(theShader);
    rect(0, 0, width, height);
}
// Handle turbulence animations
function handleTurbulenceAnimations() {
    // Layer 1 turbulence
    if (state.click_t1_turbulance) {
        if (t1_up && tmp_t1_turb <= t1_ceiling) {
            tmp_t1_turb += t1_increment;
            tmp_t1_turb = Number(tmp_t1_turb.toFixed(5));
            if (tmp_t1_turb >= t1_ceiling) t1_up = false;
        } else {
            t1_up = false;
            tmp_t1_turb -= t1_increment;
            tmp_t1_turb = Number(tmp_t1_turb.toFixed(5));
            if (tmp_t1_turb <= config.turbulence.t1_min) {
                t1_up = true;
                state.click_t1_turbulance = false;
            }
        }
        shaderParams.L1Turbulance = Number(tmp_t1_turb.toFixed(5));
    }
    // Layer 2 turbulence
    if (state.click_t2_turbulance) {
        if (t2_up && tmp_t2_turb <= t2_ceiling) {
            tmp_t2_turb += t2_increment;
            tmp_t2_turb = Number(tmp_t2_turb.toFixed(5));
            if (tmp_t2_turb >= t2_ceiling) t2_up = false;
        } else {
            t2_up = false;
            tmp_t2_turb -= t2_increment;
            tmp_t2_turb = Number(tmp_t2_turb.toFixed(5));
            if (tmp_t2_turb <= config.turbulence.t2_min) {
                t2_up = true;
                state.click_t2_turbulance = false;
            }
        }
        shaderParams.L2Turbulance = Number(tmp_t2_turb.toFixed(5));
    }
}
// Handle scroll animations
function handleScrollAnimations() {
    if (state.scroll_lerping_current) {
        if (up && tmp_shader_speed <= ceiling) {
            tmp_shader_speed += increment;
            tmp_shader_speed = Number(tmp_shader_speed.toFixed(4));
            if (tmp_shader_speed >= ceiling) up = false;
        } else {
            up = false;
            tmp_shader_speed -= increment;
            tmp_shader_speed = Number(tmp_shader_speed.toFixed(4));
            if (tmp_shader_speed <= 0.05) {
                up = true;
                state.scroll_lerping_current = false;
            }
        }
        shaderParams.iSpeed = Number(tmp_shader_speed.toFixed(4));
    }
}
// Handle layer scale animations
function handleLayerScaleAnimations() {
    // Layer 1 scale animation
    if (state.l1_lerping_current) {
        l1_map = map(allTogether.count, 1.0, config.scale.user_max, l1_floor, l1_ceiling);
        if (state.l1_up && l1_tmp <= l1_map) {
            l1_tmp += l1_increment;
            l1_tmp = Number(l1_tmp.toFixed(4));
            if (l1_tmp >= l1_map) {
                state.l1_up = false;
                state.l1_lerping_current = false;
            }
        } else {
            l1_tmp -= l1_increment;
            l1_tmp = Number(l1_tmp.toFixed(4));
            if (l1_tmp <= l1_map) {
                state.l1_up = true;
                state.l1_lerping_current = false;
            }
        }
        shaderParams.L1Scale = constrain(l1_tmp, l1_floor, l1_ceiling);
    }
    // Layer 2 scale animation
    if (state.l2_lerping_current) {
        l2_map = map(allTogether.count, 0.5, 50.0, l2_floor, l2_ceiling);
        if (state.l2_up && l2_tmp <= l2_map) {
            l2_tmp += l2_increment;
            l2_tmp = Number(l2_tmp.toFixed(4));
            if (l2_tmp >= l2_map) {
                state.l2_up = false;
                state.l2_lerping_current = false;
            }
        } else {
            l2_tmp -= l2_increment;
            l2_tmp = Number(l2_tmp.toFixed(4));
            if (l2_tmp <= l2_map) {
                state.l2_up = true;
                state.l2_lerping_current = false;
            }
        }
        // Fixed the bug: should be setting L2Scale to the lerped l2_tmp, not directly to l2_map
        shaderParams.L2Scale = constrain(l2_tmp, l2_floor, l2_ceiling);
    }
}
// P5.js windowResized function
function windowResized() {
    resizeCanvas(windowWidth, windowHeight);
}
// Add time-of-day color changes
function updateTimeBasedColors() {
    const hour = new Date().getHours();
    // Gradually transition colors based on time of day
    let newLayer1Color, newLayer2Color;
    // Morning colors (5am-11am)
    if (hour >= 5 && hour < 11) {
        newLayer1Color = [
            0.1,
            0.7,
            1.0
        ]; // Bright blue
        newLayer2Color = [
            1.0,
            1.0,
            1.0
        ]; // White
    } else if (hour >= 11 && hour < 17) {
        newLayer1Color = [
            0.0,
            0.9,
            0.7
        ]; // Teal
        newLayer2Color = [
            1.0,
            0.95,
            0.8
        ]; // Cream
    } else if (hour >= 17 && hour < 21) {
        newLayer1Color = [
            0.5,
            0.3,
            0.8
        ]; // Purple
        newLayer2Color = [
            1.0,
            0.7,
            0.4
        ]; // Orange
    } else {
        newLayer1Color = [
            0.1,
            0.2,
            0.5
        ]; // Dark blue
        newLayer2Color = [
            0.3,
            0.3,
            0.5
        ]; // Dark purple
    }
    // Gradually transition to new colors - use a small lerp value for smooth transitions
    shaderParams.Layer1CloudColour = customLerpColor(shaderParams.Layer1CloudColour, newLayer1Color, 0.002);
    shaderParams.Layer2CloudColour = customLerpColor(shaderParams.Layer2CloudColour, newLayer2Color, 0.002);
    // Log color changes less frequently (once every 10 seconds)
    if (frameCount % 300 === 0) console.log("Time-based colors updated:", hour, "hours");
}
// Simple color lerp function
function customLerpColor(colorA, colorB, amount) {
    const result = [];
    for(let i = 0; i < colorA.length; i++)result[i] = colorA[i] + (colorB[i] - colorA[i]) * amount;
    return result;
}
// Handle mouse interactions with simple lerping of scales
function handleMouseInteractions() {
    // If mouse scale effect is disabled in the GUI, don't process further
    if (!shaderParams.enableMouseScale) return;
    // Calculate mouse movement speed
    const mouseMovementX = Math.abs(mouseX - prevMouseX);
    prevMouseX = mouseX;
    // Scale and smooth the mouse speed
    mouseSpeed = mouseSpeed * 0.9 + mouseMovementX * 0.1;
    // Initialize base scales if not set
    if (!baseL1Scale) baseL1Scale = shaderParams.L1Scale;
    if (!baseL2Scale) baseL2Scale = shaderParams.L2Scale;
    // Activate mouse influence if speed is above threshold
    if (mouseSpeed > 5 && state.art_scope !== 2) {
        mouseInfluenceActive = true;
        mouseInfluenceStrength = Math.min(mouseInfluenceStrength + 0.05, 1.0);
    }
    // Apply decay to mouse influence
    if (mouseInfluenceActive) {
        mouseInfluenceStrength *= mouseInfluenceDecay;
        if (mouseInfluenceStrength < 0.01) {
            mouseInfluenceActive = false;
            mouseInfluenceStrength = 0;
            // Reset to base values when influence ends
            shaderParams.L1Scale = baseL1Scale;
            shaderParams.L2Scale = baseL2Scale;
        } else {
            // Simple lerp: increase scales by up to 25% based on influence strength
            shaderParams.L1Scale = baseL1Scale * (1 + mouseInfluenceStrength * 0.25);
            shaderParams.L2Scale = baseL2Scale * (1 + mouseInfluenceStrength * 0.25);
            // If in global mode, share this with other users
            if (state.art_scope === 0 && frameCount % 10 === 0) // Broadcast mouse influence events
            sendMouseInfluenceUpdate(mouseInfluenceStrength);
        }
    }
}
// Function to handle received mouse interactions from other users
function handleReceivedMouseInteractions(content) {
    // Only process if mouse scaling is enabled and not currently locked
    if (!shaderParams.enableMouseScale || remoteInfluenceLock || !content.interactions) return;
    // Check if there's mouse influence data in the message
    if (content.interactions.mouse_influence > 0) {
        // Lock to prevent new animations until this one completes
        remoteInfluenceLock = true;
        // Store original scale
        const originalScale = shaderParams.L2Scale;
        // Use a fixed scaling amount for more consistency
        // Can be adjusted as needed
        const scaleIncrease = 0.5;
        const targetScale = originalScale + scaleIncrease;
        // Set up animation state
        remoteAnimationState = {
            originalScale: originalScale,
            targetScale: targetScale,
            currentScale: originalScale,
            phase: "up"
        };
        // Start the animation in the draw loop
        startRemoteAnimation();
    }
}
// Function to start the remote animation process
function startRemoteAnimation() {
    // This just ensures the animation is processed in the draw loop
    if (!remoteAnimationActive) remoteAnimationActive = true;
}
// Call this in your draw loop to handle the animation
function updateRemoteAnimation() {
    if (!remoteAnimationState || !remoteAnimationActive) return;
    if (remoteAnimationState.phase === "up") {
        // Scale up phase - lerp towards target
        remoteAnimationState.currentScale = lerp(remoteAnimationState.currentScale, remoteAnimationState.targetScale, 0.1 // Adjust this value for speed - higher = faster
        );
        // Apply the current scale
        shaderParams.L2Scale = remoteAnimationState.currentScale;
        // If we're close enough to target, switch to down phase
        if (Math.abs(remoteAnimationState.currentScale - remoteAnimationState.targetScale) < 0.01) remoteAnimationState.phase = "down";
    } else if (remoteAnimationState.phase === "down") {
        // Scale down phase - lerp back to original
        remoteAnimationState.currentScale = lerp(remoteAnimationState.currentScale, remoteAnimationState.originalScale, 0.1 // Make down phase a bit slower for natural feel
        );
        // Apply the current scale
        shaderParams.L2Scale = remoteAnimationState.currentScale;
        // If we're close enough to original, end animation
        if (Math.abs(remoteAnimationState.currentScale - remoteAnimationState.originalScale) < 0.01) {
            // Reset exactly to original
            shaderParams.L2Scale = remoteAnimationState.originalScale;
            // Clear animation state and unlock
            remoteAnimationState = null;
            remoteInfluenceLock = false;
            remoteAnimationActive = false;
        }
    }
}
// Simple lerp function
function lerp(start, end, amount) {
    return start + (end - start) * amount;
}
// Function to broadcast mouse influence to other users
function sendMouseInfluenceUpdate(strength) {
    if (socket && socket.connected) {
        // Create a fresh message just for this update
        // Note: We don't set _sp flag here since mouse movements
        // shouldn't trigger DB writes
        const mouseMessage = {
            client: "soft-presence",
            v_user: userHash,
            path: window.location.pathname + window.location.hash,
            timestamp: Date.now(),
            close: 0,
            _sp: false,
            interactions: {
                mouse: [
                    mouseX,
                    mouseY
                ],
                mouse_influence: strength,
                move: true,
                scroll_active: false,
                click: 0
            }
        };
        socket.emit("message", {
            project_id: config.socket.project_id,
            channel: config.socket.channel,
            content: mouseMessage
        });
    }
}
// Function to update cloud density with dynamic soft cap
function updateCloudDensityForVisitorCount(count) {
    // Ensure minimum of 1 visitor
    count = Math.max(1, count);
    // Update our peak visitor count if needed
    if (count > peakVisitorCount) {
        peakVisitorCount = count;
        // Gradually adapt our soft cap toward peak * adjustment factor
        // This makes the soft cap trail slightly behind the peak
        const targetSoftCap = peakVisitorCount * 1.2; // 20% above current peak
        // Move soft cap toward target
        dynamicSoftCap = lerp(dynamicSoftCap, targetSoftCap, densityConfig.adaptationRate);
        // Enforce min/max bounds
        dynamicSoftCap = Math.max(densityConfig.minSoftCap, Math.min(densityConfig.maxSoftCap, dynamicSoftCap));
    } else if (count < peakVisitorCount * 0.5) {
        // If visitor count drops significantly (below 50% of peak)
        // Start lowering the peak and soft cap to adapt to the new reality
        peakVisitorCount = lerp(peakVisitorCount, count, 0.01); // Very slow adaptation down
        // Adapt soft cap downward more slowly than upward
        // This creates a "memory" of busy periods that fades gradually
        const targetSoftCap = peakVisitorCount * 1.2;
        dynamicSoftCap = lerp(dynamicSoftCap, targetSoftCap, densityConfig.adaptationRate * 0.2 // 5x slower adaptation downward
        );
        // Enforce minimum soft cap
        dynamicSoftCap = Math.max(densityConfig.minSoftCap, dynamicSoftCap);
    }
    // Calculate impact using our dynamic soft cap
    const logBase = 1.1;
    const logMax = Math.log(dynamicSoftCap) / Math.log(logBase);
    const logCurrent = Math.log(Math.min(count, dynamicSoftCap * 2)) / Math.log(logBase);
    // Calculate normalized impact (0 to 1)
    let impact = logCurrent / logMax;
    impact = Math.min(1.0, impact);
    // Update shader parameters
    shaderParams.Amplitude = lerp(densityConfig.amplitude.min, densityConfig.amplitude.max, impact);
    shaderParams.Hvalue = lerp(densityConfig.baseNoise.min, densityConfig.baseNoise.max, impact);
    if (_DEBUG) console.log(`Visitors: ${count}, Soft Cap: ${dynamicSoftCap.toFixed(1)}, Peak: ${peakVisitorCount.toFixed(1)}, Impact: ${impact.toFixed(2)}`);
}
// Function to fetch statistics from the server
async function fetchStatistics() {
    try {
        // console.log("Fetching statistics from server...");
        const response = await fetch(`${getServerUrl()}/api/soft-presence/stats`);
        if (!response.ok) throw new Error(`Server responded with ${response.status}`);
        const stats = await response.json();
        // Update all stats displays
        updateStatsDisplay(stats);
        return stats;
    } catch (error) {
        console.error("Error fetching statistics:", error);
        return null;
    }
}
// Update all stats display elements with server data
function updateStatsDisplay(stats) {
    if (!stats) return;
    const viewerCount = allTogether.count;
    // Update both the main stats and flyout stats
    document.querySelectorAll("#viewer-count, #flyout-viewers span").forEach((el)=>{
        if (el) el.textContent = viewerCount;
    });
    // 2. Total pulses
    const totalPulses = stats.totals?.total_pulses || 0;
    if (_DEBUG) console.log(`Setting total pulses to: ${totalPulses}`);
    document.querySelectorAll("#total-pulses, #flyout-total-pulses span").forEach((el)=>{
        if (el) {
            if (_DEBUG) console.log(`Updating element ${el.id || "unknown"} with total pulses ${totalPulses}`);
            el.textContent = totalPulses;
        } else console.log("Element for total pulses not found");
    });
    // Update locations count from server data
    const locationElements = document.querySelectorAll("#location-count, #flyout-locations span");
    locationElements.forEach((el)=>{
        if (el) el.textContent = stats.current.locations || stats.totals.total_locations || "0";
    });
    // Update pulses for today
    const pulseElements = document.querySelectorAll("#cloud-pulses, #flyout-pulses span");
    pulseElements.forEach((el)=>{
        if (el) el.textContent = stats.today.pulses || "0";
    });
    // Update total pulses
    const totalPulseElements = document.querySelectorAll("#total-pulses");
    totalPulseElements.forEach((el)=>{
        if (el) el.textContent = stats.totals.total_pulses || "0";
    });
    // Update last pulse time
    if (stats.lastPulse) {
        const lastPulseTime = new Date(stats.lastPulse);
        const now = new Date();
        const diffMs = now - lastPulseTime;
        const diffMinutes = Math.floor(diffMs / 60000);
        const diffSeconds = Math.floor(diffMs % 60000 / 1000);
        const lastPulseElements = document.querySelectorAll("#last-pulse-time, #flyout-last-pulse span");
        lastPulseElements.forEach((el)=>{
            if (el) {
                if (diffMinutes < 1 && diffSeconds < 5) el.textContent = "just now";
                else if (diffMinutes < 1) el.textContent = `${diffSeconds} sec ago`;
                else if (diffMinutes === 1) el.textContent = "1 minute ago";
                else el.textContent = `${diffMinutes} minutes ago`;
            }
        });
    }
}

//# sourceMappingURL=SoftPresence.5fc89419.js.map
